import './styles/App.css';
import { FormEvent, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { api } from './api';
import { SiteLayout } from './components/SiteLayout';
import { CatalogPage } from './pages/CatalogPage';
import { ItemPage } from './pages/ItemPage';
import { RankingsPage } from './pages/RankingsPage';
import { PlayerPage } from './pages/PlayerPage';

type AuthState = 'checking' | 'locked' | 'unlocked';

function App() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [keyInput, setKeyInput] = useState('');
  const [submittingKey, setSubmittingKey] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const existing = api.getApiKey();
      if (!existing) {
        if (!cancelled) setAuthState('locked');
        return;
      }

      const valid = await api.validateApiKey(existing);
      if (cancelled) return;

      if (valid) {
        setAuthState('unlocked');
        return;
      }

      api.clearApiKey();
      setAuthState('locked');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleKeySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const candidate = keyInput.trim();
    if (!candidate) {
      setKeyError('Enter an API key.');
      return;
    }

    setSubmittingKey(true);
    setKeyError(null);

    try {
      const valid = await api.validateApiKey(candidate);
      if (!valid) {
        setKeyError('Invalid API key.');
        return;
      }

      api.setApiKey(candidate);
      setAuthState('unlocked');
      setKeyInput('');
    } catch {
      setKeyError('Could not validate key against the backend.');
    } finally {
      setSubmittingKey(false);
    }
  }

  const routes = (
    <Routes>
      <Route path="/" element={<CatalogPage />} />
      <Route path="/items/:itemKey" element={<ItemPage />} />
      <Route path="/rankings" element={<RankingsPage />} />
      <Route path="/players/:robloxId" element={<PlayerPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return (
    <SiteLayout>
      {authState === 'unlocked' ? routes : <LockedLanding isChecking={authState === 'checking'} />}

      {authState !== 'unlocked' && (
        <div className="gate-modal__backdrop">
          <div className="gate-modal">
            <p className="eyebrow">Access Required</p>
            <h2>Enter API key to unlock data</h2>
            <p className="gate-modal__copy">
              The landing screen is public, but all catalog, ownership, and ranking data requires a valid key.
            </p>

            <form onSubmit={handleKeySubmit} className="gate-modal__form">
              <input
                type="password"
                placeholder="API key"
                value={keyInput}
                onChange={(event) => setKeyInput(event.target.value)}
                className="search-input"
                disabled={submittingKey || authState === 'checking'}
              />
              <button className="button" type="submit" disabled={submittingKey || authState === 'checking'}>
                {submittingKey ? 'Validating...' : 'Unlock'}
              </button>
            </form>

            {keyError ? <p className="gate-modal__error">{keyError}</p> : null}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}

function LockedLanding({ isChecking }: { isChecking: boolean }) {
  return (
    <div className="page-stack">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Flee The Facility</p>
          <h1>Powering no privacy</h1>
          <p className="hero-copy">
            {isChecking
              ? 'Checking saved API key...'
              : 'Enter your API key to load items, holders, and collector rankings.'}
          </p>
        </div>
      </section>
    </div>
  );
}

export default App;
